/**
 * @file Протокол входа по одноразовому токену из фрагмента URL.
 *
 * Последовательность: прочитать токен, обменять его на сессию,
 * проверить данные сессии. Автоматического повтора обмена нет: потерянный ответ
 * не доказывает, что сервер не использовал токен.
 */
import type { CustomerInfo } from '../../context/useCustomer'
import { fetchSession } from './api.ts'
import { SignInError } from './signInError.ts'

export type OneTimeSignInErrorCode =
    | 'invalidLink'
    | 'expired'
    | 'rateLimited'
    | 'unavailable'
    | 'invalidSession'
    | 'network'
    | 'timeout'

export class OneTimeSignInError extends Error {
    readonly code: OneTimeSignInErrorCode

    constructor(code: OneTimeSignInErrorCode) {
        super(code)
        this.name = 'OneTimeSignInError'
        this.code = code
    }
}

/** Возвращает код для перевода ошибки, не раскрывая её текст или токен в UI. */
export function oneTimeSignInErrorCode(error: unknown): OneTimeSignInErrorCode {
    if (error instanceof OneTimeSignInError) return error.code

    if (error instanceof DOMException && error.name === 'TimeoutError')
        return 'timeout'

    return 'network'
}

/** Читает токен из location.hash; проверяем только его наличие и непустое значение. */
export function parseOneTimeToken(fragment: string): string {
    // URLSearchParams снимает URL-кодирование один раз. Формат токена
    // и срок его действия проверяет backend, поэтому содержимое не меняем.
    const token = new URLSearchParams(fragment.slice(1)).get('token')
    if (!token) throw new OneTimeSignInError('invalidLink')

    return token
}

/**
 * Забирает токен из адреса до запуска React и создаёт попытку входа без запросов.
 * Для остальных страниц возвращает null, чтобы работало обычное восстановление сессии.
 */
export function takeOneTimeSignIn(
    location: Pick<Location, 'pathname' | 'hash'>,
    history: Pick<History, 'replaceState' | 'state'>,
): OneTimeSignInAttempt | null {
    if (!['/otat', '/otat/'].includes(location.pathname)) return null

    const fragment = location.hash
    // Даже некорректная ссылка не должна оставлять секрет в адресе.
    // Query тоже убираем, но токен принимаем только из фрагмента.
    history.replaceState(history.state, '', location.pathname)
    // Даже пустой фрагмент на /otat получает попытку: её разбор покажет invalidLink.
    // Возврат null включил бы восстановление старой сессии вместо ошибки ссылки.
    return createOneTimeSignInAttempt(fragment)
}

/** Один раз обменивает OTAT на сессию и возвращает проверенные данные пользователя. */
export async function signInByOneTimeLink(
    fragment: string,
    signal: AbortSignal,
): Promise<CustomerInfo> {
    signal.throwIfAborted()
    const token = parseOneTimeToken(fragment)

    // Токен отправляется только API сессии, без автоматических повторов и редиректов.
    const response = await fetch('/user-api/customer/session/otat', {
        method: 'POST',
        headers: {
            'X-One-Time-Auth-Token': token,
            'Content-Type': 'application/json',
        },
        credentials: 'same-origin',
        redirect: 'error',
        cache: 'no-store',
        referrerPolicy: 'no-referrer',
        signal,
    })
    if ([401, 403, 404, 410].includes(response.status))
        throw new OneTimeSignInError('expired')

    if (response.status === 429) throw new OneTimeSignInError('rateLimited')

    if (!response.ok) throw new OneTimeSignInError('unavailable')

    // Сессия должна существовать и иметь ожидаемую форму, прежде чем откроется dashboard.
    try {
        return await fetchSession(signal)
    } catch (error) {
        signal.throwIfAborted()
        if (error instanceof SignInError)
            throw new OneTimeSignInError('invalidSession')

        throw error
    }
}

/**
 * Создаёт одну попытку входа для одного токена, но пока не делает запрос.
 * Первый acquire() запускает run(), следующие получают тот же Promise —
 * объект будущего результата. Успех и ошибка сохраняются в нём: это не retry.
 * Параметр run позволяет тесту заменить настоящие запросы простой функцией.
 *
 * Зачем нужен подсчёт subscribers: в StrictMode React проверяет эффект так:
 *   setup A → cleanup A → setup B → выполнение микрозадач.
 * acquire A: 0 → 1, release A: 1 → 0, acquire B: 0 → 1.
 * Отложенная проверка видит 1, поэтому запрос не отменяется и не повторяется.
 * При настоящем уходе setup B уже не будет: остаётся 0, запрос отменяется.
 * Микрозадача — callback queueMicrotask, выполняемый после текущего
 * синхронного кода, до следующего обычного события/таймера.
 *
 * release не отменяет запрос сразу. Если сделать это прямо в cleanup A,
 * setup B получит уже отменённую попытку, хотя страница по-прежнему открыта.
 * Объект создаётся при загрузке страницы, до React, и передаётся экрану OTAT.
 * После ухода с экрана повторное использование результата запрещено.
 */
export function createOneTimeSignInAttempt(
    fragment: string,
    run = signInByOneTimeLink,
) {
    // Эти переменные принадлежат объекту попытки и общие для всех acquire().
    const controller = new AbortController()
    // null только до первого запуска. Отклонённый Promise тоже сохраняем:
    // ошибка запроса не означает, что одноразовый токен остался неиспользованным.
    let promise: Promise<CustomerInfo> | null = null
    let subscribers = 0

    return {
        acquire() {
            // После настоящего ухода со страницы нельзя ни повторить обмен,
            // ни заново опубликовать результат уже завершённой попытки.
            if (controller.signal.aborted) {
                return {
                    promise: Promise.reject<CustomerInfo>(
                        new OneTimeSignInError('invalidLink'),
                    ),
                    release() {},
                }
            }

            subscribers++

            // StrictMode присоединяется к той же попытке без повторного POST.
            if (!promise) {
                promise = run(
                    fragment,
                    AbortSignal.any([
                        controller.signal,
                        AbortSignal.timeout(30_000),
                    ]),
                )
                // Запрос уже получил значение; исходную ссылку больше не удерживаем.
                fragment = ''
            }

            // В отличие от subscribers, этот флаг отдельный у каждого acquire.
            // Он не даёт одному cleanup дважды уменьшить общий счётчик.
            let released = false

            return {
                promise,

                release() {
                    if (released) return

                    released = true
                    subscribers--

                    // StrictMode успевает подписаться повторно в том же цикле.
                    // Настоящий уход со страницы оставит ноль подписчиков и отменит запрос.
                    queueMicrotask(() => {
                        if (subscribers === 0) controller.abort()
                    })
                },
            }
        },
    }
}

export type OneTimeSignInAttempt = ReturnType<typeof createOneTimeSignInAttempt>
